import { useCallback, useEffect, useMemo } from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import path from "path-browserify";

import { requestWrapper } from "~/app/utils/requests";

import {
  DEFAULT_LANGUAGE,
  DEFAULT_LANGUAGE_WITHOUT_REGION,
} from "~/app/constant";
import { BASE_URL } from "~/app/constant/env";

const GLOBAL_DATAS_STALE_TIME = 1000 * 60 * 5; // 5 minutes

const getParentRole = (role) => {
  switch (role) {
    case "Reporter":
      return undefined;
    case "User":
      return "Reporter";
    default:
      return "User";
  }
};

const apiGetAppData = () =>
  requestWrapper.get(`/c13s/appdata`).then((res) => res.data);

const getAppConfig = () =>
  axios
    .get(path.join(BASE_URL, "custom-app-config.json"))
    .then((res) => res.data)
    .catch((err) => {
      if (err.response.status === 404) {
        // If the config file doesn't exist, return an empty object
        return {};
      } else {
        throw err;
      }
    });

const apiGetCounters = () =>
  requestWrapper.get(`/c13s/counters`).then((res) => res.data);

const apiGetSourcesApi = () =>
  requestWrapper.get("/c13s/sources").then((res) => res && res.data);

const apiGetTopicsTree = () =>
  requestWrapper.get(`/c13s/taxonomy/topics`).then((res) => res && res.data);

const apiGetTopics = (languageId) =>
  requestWrapper
    .get(`/c13s/taxonomy/${languageId}/topics`)
    .then((res) => res && res.data);

const apiGetPublicConfiguration = () =>
  requestWrapper
    .get("/c13s/configuration/login")
    .then((res) => res && res.data);

const apiGetPrivateConfiguration = () =>
  requestWrapper
    .get("/c13s/configuration/application")
    .then((res) => res && res.data);

export const useAppDataQuery = () =>
  useQuery({
    queryKey: ["appdata"],
    queryFn: apiGetAppData,
    staleTime: GLOBAL_DATAS_STALE_TIME,
    select: (data) => ({
      ...data,
      roles: data.roles.map((role) => ({
        ...role,
        extends: getParentRole(role.name),
      })),
    }),
  });

export const useAppConfigQuery = () =>
  useQuery({
    queryKey: ["appconfig"],
    queryFn: getAppConfig,
    staleTime: Infinity,
  });

export const useCountersQuery = () =>
  useQuery({
    queryKey: ["counters"],
    queryFn: apiGetCounters,
    initialData: {},
    refetchInterval: 1000 * 30, //30 seconds
    staleTime: 0,
  });

export const useAmendCounter = () => {
  const queryClient = useQueryClient();
  return (counterName, valueToAdd) =>
    queryClient.setQueryData(["counters"], (prevData) => ({
      ...prevData,
      [counterName]: (prevData[counterName] || 0) + valueToAdd,
    }));
};

export const useSourcesQuery = () =>
  useQuery({
    queryKey: ["sources"],
    queryFn: apiGetSourcesApi,
    staleTime: Infinity,
  });

export const usePrefetchSources = () => {
  const queryClient = useQueryClient();
  useEffect(() => {
    queryClient.prefetchQuery({
      queryKey: ["sources"],
      queryFn: apiGetSourcesApi,
    });
  }, []);
};

export const useFrequenciesByType = () => {
  const { data: appData } = useAppDataQuery();
  const frequenciesByType = useMemo(() => {
    const frequencies = appData?.frequencies || [];
    return frequencies.reduce((acc, frequency) => {
      if (acc[frequency.type]) {
        acc[frequency.type].push(frequency);
      } else {
        acc[frequency.type] = [frequency];
      }
      return acc;
    }, {});
  }, [appData]);
  return frequenciesByType;
};

export const useGetTopicsQuery = (
  languageId = DEFAULT_LANGUAGE_WITHOUT_REGION
) =>
  useQuery({
    queryKey: ["topics", languageId],
    queryFn: () => apiGetTopics(languageId),
    staleTime: Infinity,
  });

export const useGetTopicsTreeQuery = () =>
  useQuery({
    queryKey: ["topicsTree"],
    queryFn: apiGetTopicsTree,
    staleTime: Infinity,
  });

const flattenTopicsTree = (topicsTreeData) => {
  const result = [];

  const pushTopics = (topics, level, language, parents = []) => {
    topics.forEach((topic) => {
      result.push({
        label: topic.label,
        id: topic.topic_id,
        level,
        language, // Include the language key,
        parents,
      });
      const childrenTopics = topic[`topics_${level + 1}`];
      if (childrenTopics) {
        const chlidrenParents = [...parents, topic.topic_id];
        pushTopics(childrenTopics, level + 1, language, chlidrenParents);
      }
    });
  };

  topicsTreeData.forEach((lang) => {
    pushTopics(lang.topics, 1, lang.code);
  });

  return result;
};

export const useGetTopicsFlatQuery = () => {
  const topicsTreeQuery = useGetTopicsTreeQuery();
  const data = topicsTreeQuery?.data && flattenTopicsTree(topicsTreeQuery.data);
  return {
    ...topicsTreeQuery,
    data,
  };
};

export const useGetFilteredTopics = () => {
  const flatTopicsQuery = useGetTopicsFlatQuery();

  return useCallback(
    (topicIds) =>
      topicIds
        ?.map((topic) =>
          flatTopicsQuery.data?.find((flatTopic) => flatTopic.id === topic)
        )
        ?.filter((it) => it),
    [flatTopicsQuery]
  );
};

export const usePublicConfigurationQuery = () => {
  const query = useQuery({
    queryKey: ["publicConfiguration"],
    queryFn: apiGetPublicConfiguration,
    select: (data) => {
      return {
        ...data,
        language: data.language || DEFAULT_LANGUAGE,
        language_without_region:
          data.language?.substring(0, 2) || DEFAULT_LANGUAGE_WITHOUT_REGION,
      };
    },
    staleTime: GLOBAL_DATAS_STALE_TIME,
  });

  return query;
};

export const useConfigurationQuery = () =>
  useQuery({
    queryKey: ["configuration"],
    queryFn: apiGetPrivateConfiguration,
    staleTime: GLOBAL_DATAS_STALE_TIME,
  });
